import { format, formatDistanceToNow } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminWebhooks } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';

const EVENT_OPTIONS = [
  'webhook.test',
  'customer.created',
  'customer.updated',
  'payment.abandoned',
  'payment.created',
  'payment.paid',
  'payment.canceled',
  'booking.abandoned',
  'booking.created',
  'booking.paid',
  'booking.canceled',
  'ticket.created',
  'ticket.canceled',
  'submission.created',
  'submission.updated',
  'submission.canceled',
  'checkout_page.created',
  'checkout_page.updated',
  'product.created',
  'product.updated',
  'subscription.abandoned',
  'subscription.created',
  'subscription.trialing',
  'subscription.scheduled',
  'subscription.active',
  'subscription.completed',
  'subscription.canceled',
  'subscription.expired',
];

type WebhookRow = {
  _id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive' | 'failed';
  deliveryCount: number;
  successCount: number;
  failureCount: number;
  lastTriggeredAt?: string | null;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  isDeleted?: boolean;
  createdAt: string;
  seller?: { _id: string; displayName?: string; name?: string } | null;
};

const statusBadgeVariant = (status: string): 'secondary' | 'outline' | 'destructive' => {
  if (status === 'failed') return 'destructive';
  if (status === 'inactive') return 'outline';
  return 'secondary';
};

const Webhooks = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [event, setEvent] = useState(query.event || '');
  const [status, setStatus] = useState(query.status || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(event && { event }),
        ...(status && { status }),
      }),
    });
  }, [sortBy, sortDirection, search, event, status]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    event,
    status,
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminWebhooks(filters);

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData: WebhookRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Name" />,
        cell: ({ row }: any) => <span>{row.original.name}</span>,
        enableSorting: true,
      },
      {
        accessorKey: 'url',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="URL" />,
        cell: ({ row }: any) => (
          <Tooltip label={row.original.url} side="bottom">
            <code className="text-xs bg-zinc-100 dark:bg-muted px-1.5 py-0.5 rounded cursor-help block max-w-xs truncate">
              {row.original.url}
            </code>
          </Tooltip>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Seller" />,
        cell: ({ row }: any) => {
          const webhook = row.original;
          if (!webhook.seller) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Link href={`/admin/merchants/${webhook.seller._id}`}>
              {webhook.seller.displayName || webhook.seller.name}
            </Link>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'events',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Events" />,
        cell: ({ row }: any) => {
          const events: string[] = row.original.events || [];
          if (events.length === 0) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Tooltip label={events.join(', ')} side="bottom">
              <Badge variant="outline" className="cursor-help">
                {events.length} {events.length === 1 ? 'event' : 'events'}
              </Badge>
            </Tooltip>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'deliveryCount',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Deliveries" />,
        cell: ({ row }: any) => {
          const total: number = row.original.deliveryCount || 0;
          const success: number = row.original.successCount || 0;
          const failure: number = row.original.failureCount || 0;
          if (total === 0) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Tooltip
              label={`${success.toLocaleString()} success, ${failure.toLocaleString()} failure`}
              side="bottom"
            >
              <span className="cursor-help">{total.toLocaleString()}</span>
            </Tooltip>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'lastTriggeredAt',
        header: ({ column }: any) => (
          <DataGridColumnHeader column={column} title="Last triggered" />
        ),
        cell: ({ row }: any) => {
          const lastTriggeredAt = row.original.lastTriggeredAt;
          if (!lastTriggeredAt)
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          const date = new Date(lastTriggeredAt);
          return (
            <Tooltip label={format(date, 'dd MMM yyyy HH:mm')} side="bottom">
              <span className="cursor-help">{formatDistanceToNow(date, { addSuffix: true })}</span>
            </Tooltip>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Created" />,
        cell: ({ row }: any) => {
          const createdAt = row.original.createdAt;
          return createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) => {
          const webhook = row.original;
          if (webhook.isDeleted) {
            return <Badge variant="destructive">Deleted</Badge>;
          }
          const label =
            webhook.status === 'active'
              ? 'Active'
              : webhook.status === 'inactive'
                ? 'Inactive'
                : webhook.status === 'failed'
                  ? 'Failed'
                  : webhook.status;
          return <Badge variant={statusBadgeVariant(webhook.status)}>{label}</Badge>;
        },
        enableSorting: false,
      },
    ],
    [],
  );

  const total = data?.pages[0]?.total;

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
        {total} webhooks
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select
          name="status"
          value={status || 'all'}
          onValueChange={value => setStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="event">Event</Label>
        <Select
          name="event"
          value={event || 'all'}
          onValueChange={value => setEvent(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {EVENT_OPTIONS.map(ev => (
              <SelectItem key={ev} value={ev}>
                {ev}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={(e: any) => setSearchInput(e.target.value)}
              placeholder="Search by name or URL"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Webhooks</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No webhooks found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default Webhooks;
